import type { HttpClient } from '../client/http';
import type { TrackedEvent } from '../analytics-events';

/**
 * Trusted-code analytics emitter — bypasses the bundle-facing allowlist
 * validator (`stubs/track.ts`) because every event below is itself part of
 * `ALLOWED_EVENTS`. Direct `window.posthog.capture` keeps auth flows free of
 * cross-module deps and works pre-React (when `window.posthog` is set by the
 * shell's `initPostHog` callback).
 */
function authTrack(event: TrackedEvent, props?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  const ph = (window as unknown as { posthog?: { capture?: (e: string, p?: Record<string, unknown>) => void } }).posthog;
  if (typeof ph?.capture !== 'function') return;
  try {
    ph.capture(event, props);
  } catch {
    /* noop — analytics never breaks auth */
  }
}

export interface OAuthRedirectArgs {
  /**
   * URL to return to after the OAuth round-trip. Defaults to
   * `window.location.href`. Must be allowlisted by the backend
   * (`backend/src/auth/oauth/returnTo.ts`) — for the MVP that means
   * `https://{PUBLIC_HOST}/app/{appSlug}/...`.
   */
  returnTo?: string;
}

/**
 * Plan-V — pay-first checkout result returned by `subscribeAnonymous`.
 * Card path includes session-only fields (cookies were set by Set-Cookie
 * headers); PIX path additionally returns the QR payload so the UI can
 * render the waiting page.
 */
export interface SubscribeAnonymousResult {
  subscription_id: string;
  redirect: string;
  pix_qr_payload?: string | null;
  pix_qr_base64?: string | null;
  pix_authorization_id?: string;
}

export interface SubscribeAnonymousCardArgs {
  method: 'card';
  name: string;
  email: string;
  emailConfirm: string;
  phone: string;
  cpf: string;
  cycle: 'MONTHLY' | 'YEARLY';
  card: {
    holderName: string;
    number: string;
    expiryMonth: string;
    expiryYear: string;
    ccv: string;
  };
  cardHolderInfo: {
    name: string;
    email: string;
    cpfCnpj: string;
    postalCode: string;
    addressNumber: string;
    phone?: string;
  };
}

export interface SubscribeAnonymousPixArgs {
  method: 'pix-auto';
  name: string;
  email: string;
  emailConfirm: string;
  phone: string;
  cpf: string;
  cycle: 'MONTHLY' | 'YEARLY';
}

export type SubscribeAnonymousArgs =
  | SubscribeAnonymousCardArgs
  | SubscribeAnonymousPixArgs;

/**
 * Plan-V — thrown when the server returns 409 from /payments/checkout/subscribe
 * indicating the email is already registered for this app. Callers should
 * redirect to the login page (login_url is provided).
 */
export class EmailTakenError extends Error {
  constructor(public readonly loginUrl: string, public readonly email: string) {
    super('Email already registered');
    this.name = 'EmailTakenError';
  }
}

export interface AuthMethods {
  login(args: { email: string; password: string }): Promise<void>;
  signup(args: { email: string; password: string; name?: string }): Promise<void>;
  logout(): Promise<void>;
  logoutAll(): Promise<void>;
  forgot(args: { email: string }): Promise<void>;
  /**
   * Reset de senha via token JWT do email. Token contém `app_id` claim;
   * não precisa passar explícito. Backend valida JWT + `pwChangedAt`
   * (invalidador de uso único) e revoga todos os refresh tokens do user.
   */
  reset(args: { token: string; newPassword: string }): Promise<void>;
  resendVerify(): Promise<void>;
  changePassword(args: { oldPassword: string; newPassword: string }): Promise<void>;
  changeEmail(args: { newEmail: string }): Promise<void>;
  refresh(): Promise<void>;
  /**
   * Redireciona pro `/api/auth/oauth/google/start` do backend. O backend
   * gera state JWT (10min TTL) com `appSlug` + `nonce` + `returnTo`, bate em
   * `accounts.google.com/o/oauth2/v2/auth`, e após consentimento volta pra
   * `returnTo` com cookies de sessão setados. Fire-and-forget: a página
   * é descarregada antes de qualquer retorno.
   */
  loginWithGoogle(args?: OAuthRedirectArgs): void;
  /**
   * Mesma coisa que `loginWithGoogle`, mas com `?link=1` — usado quando o
   * usuário já tá logado (via senha) e quer adicionar Google como método
   * alternativo. Backend valida via `linkPolicy.linkAdditional`.
   */
  linkGoogle(args?: OAuthRedirectArgs): void;
  /**
   * Plan-V — pay-first signup. Creates the user + Asaas subscription server-
   * side in a single transaction; cookies are set by the response. Throws
   * `EmailTakenError` on 409 (email already registered). Caller should call
   * `refresh()` to pick up the new session (or just navigate — the cookies
   * are already set).
   */
  subscribeAnonymous(args: SubscribeAnonymousArgs): Promise<SubscribeAnonymousResult>;
  /**
   * Plan-V — POST /api/auth/email-check. Returns true if the email already
   * has an account in this app. Used by the checkout form to swap CTA to
   * "Email já cadastrado · Entrar" on email-blur.
   */
  checkEmailExists(args: { email: string }): Promise<{ exists: boolean }>;
  /**
   * Plan-V — POST /api/auth/request-access-link. Always resolves (no email
   * enumeration). If the email exists, the backend enqueues a magic-link
   * email; UI should show "Te mandamos um link de acesso".
   */
  requestAccessLink(args: { email: string }): Promise<void>;
}

export interface CreateAuthMethodsArgs {
  client: HttpClient;
  appId: string;
  appSlug: string;
  apiHost: string;
  refreshUser: () => Promise<void>;
}

export function createAuthMethods({
  client,
  appId,
  appSlug,
  apiHost,
  refreshUser,
}: CreateAuthMethodsArgs): AuthMethods {
  function oauthStartUrl(provider: 'google', opts: { link?: boolean; returnTo?: string }): string {
    // Aceita apiHost absoluto (produção/staging) ou vazio (testes com base URL
    // do jsdom). `new URL(path, base)` resolve em ambos os casos.
    const base = apiHost || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const url = new URL(`/api/auth/oauth/${provider}/start`, base);
    url.searchParams.set('app_slug', appSlug);
    url.searchParams.set(
      'return_to',
      opts.returnTo ?? (typeof window !== 'undefined' ? window.location.href : ''),
    );
    if (opts.link) url.searchParams.set('link', '1');
    return url.toString();
  }

  return {
    async login(args) {
      await client.request('POST', '/api/auth/login', { body: { app_id: appId, ...args } });
      await refreshUser();
      authTrack('login_completed', { app_id: appId, app_slug: appSlug });
    },
    async signup(args) {
      authTrack('signup_started', { app_id: appId, app_slug: appSlug });
      await client.request('POST', '/api/auth/signup', { body: { app_id: appId, ...args } });
      await refreshUser();
      authTrack('signup_completed', { app_id: appId, app_slug: appSlug });
    },
    async logout() {
      await client.request('POST', '/api/auth/logout');
      await refreshUser();
    },
    async logoutAll() {
      await client.request('POST', '/api/auth/logout-all');
      await refreshUser();
    },
    async forgot(args) {
      await client.request('POST', '/api/auth/forgot', { body: { app_id: appId, ...args } });
    },
    async reset(args) {
      // Backend espera snake_case (password.ts:74). SDK API pública é camelCase.
      // Sem credentials nem appId — token JWT no body basta.
      await client.request('POST', '/api/auth/reset', {
        body: { token: args.token, new_password: args.newPassword },
      });
    },
    async resendVerify() {
      await client.request('POST', '/api/auth/resend-verify');
    },
    async changePassword(args) {
      // Backend espera snake_case (password.ts:108-109). SDK API pública é camelCase.
      await client.request('POST', '/api/auth/change-password', {
        body: { old_password: args.oldPassword, new_password: args.newPassword },
      });
    },
    async changeEmail(args) {
      // Backend espera snake_case (email.ts:107). SDK API pública é camelCase.
      await client.request('POST', '/api/auth/change-email', {
        body: { new_email: args.newEmail },
      });
    },
    async refresh() {
      await refreshUser();
    },
    loginWithGoogle(args) {
      window.location.assign(oauthStartUrl('google', { returnTo: args?.returnTo }));
    },
    linkGoogle(args) {
      window.location.assign(oauthStartUrl('google', { returnTo: args?.returnTo, link: true }));
    },
    async subscribeAnonymous(args) {
      authTrack('signup_started', { app_id: appId, app_slug: appSlug });
      const body: Record<string, unknown> = {
        name: args.name,
        email: args.email,
        email_confirm: args.emailConfirm,
        phone: args.phone,
        cpf: args.cpf,
        cycle: args.cycle,
        method: args.method,
      };
      if (args.method === 'card') {
        body.card = args.card;
        body.card_holder_info = args.cardHolderInfo;
      }
      try {
        const result = await client.request<SubscribeAnonymousResult>(
          'POST',
          `/api/app/${appSlug}/payments/checkout/subscribe`,
          { body },
        );
        await refreshUser();
        authTrack('signup_completed', { app_id: appId, app_slug: appSlug });
        return result;
      } catch (err) {
        // HttpClient throws on non-2xx with `.status` + `.body` props on the
        // error. We translate 409 → EmailTakenError so callers can branch
        // cleanly.
        const httpErr = err as { status?: number; body?: { error?: string; login_url?: string } };
        if (httpErr?.status === 409 && httpErr?.body?.error === 'email_taken') {
          throw new EmailTakenError(httpErr.body.login_url ?? '/login', args.email);
        }
        throw err;
      }
    },
    async checkEmailExists(args) {
      return client.request<{ exists: boolean }>(
        'POST',
        '/api/auth/email-check',
        { body: { app_slug: appSlug, email: args.email } },
      );
    },
    async requestAccessLink(args) {
      await client.request<{ ok: true }>(
        'POST',
        '/api/auth/request-access-link',
        { body: { app_slug: appSlug, email: args.email } },
      );
    },
  };
}
